/**
 * ErrorBoundary — catches render-time exceptions in its subtree and shows a
 * recoverable fallback instead of crashing the whole application.
 *
 * This is primarily a safety net for real-time updates: a malformed card or
 * board payload arriving over WebSocket (including changes made through the
 * MCP server) should degrade gracefully rather than white-screen the board.
 */

import React from 'react';

export interface ErrorBoundaryProps {
  readonly children: React.ReactNode;
  /** Optional custom fallback. Receives the error and a reset callback. */
  readonly fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Optional label used in the default fallback copy (e.g. "board"). */
  readonly label?: string;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log to the console for now; a dedicated client-log endpoint can hook in here later.
    // Avoid logging anything beyond the error itself to prevent leaking payload data.
    console.error('[ErrorBoundary] Caught render error:', error.message, info.componentStack);
  }

  reset(): void {
    this.setState({ error: null });
  }

  render(): React.ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      const what = this.props.label ? ` the ${this.props.label}` : '';
      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center gap-3 p-8 text-center"
        >
          <h2 className="text-lg font-semibold text-foreground">
            Something went wrong loading{what}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            The view hit an unexpected error. This can happen after a real-time update.
            You can retry, or reload the page.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="border border-border px-3 py-1.5 rounded-md text-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
